import { useEffect } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import type { UserSummary, SharedProps, UserRole } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  user?: UserSummary | null
  organisations: { id: number | string; name: string }[]
  teams: { id: number | string; name: string; organisation_id: number | string }[]
}

export function UserFormDialog({ open, onOpenChange, user, organisations }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const isEdit = !!user

  const { data, setData, post, patch, processing, reset } = useForm({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email_address: user?.email_address || "",
    organisation_id: user?.organisation_id
      ? String(user.organisation_id)
      : current_user?.is_super_admin
        ? ""
        : String(current_user?.organisation_id || ""),
    role: user?.role || "user",
    is_active: user?.is_active ?? true,
    phone_number: user?.phone_number || "",
    job_title: user?.job_title || "",
  })

  useEffect(() => {
    setData({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email_address: user?.email_address || "",
      organisation_id: user?.organisation_id
        ? String(user.organisation_id)
        : current_user?.is_super_admin
          ? ""
          : String(current_user?.organisation_id || ""),
      role: user?.role || "user",
      is_active: user?.is_active ?? true,
      phone_number: user?.phone_number || "",
      job_title: user?.job_title || "",
    })
  }, [user])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const options = {
      onSuccess: () => {
        onOpenChange(false)
        reset()
      },
    }
    if (isEdit) {
      patch(`/users/${user!.id}`, options)
    } else {
      post("/users", options)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifier ${user!.first_name} ${user!.last_name}` : "Nouvel utilisateur"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <DialogBody className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="dlg_first_name">Prénom</Label>
              <Input
                id="dlg_first_name"
                value={data.first_name}
                onChange={(e) => setData("first_name", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dlg_last_name">Nom</Label>
              <Input
                id="dlg_last_name"
                value={data.last_name}
                onChange={(e) => setData("last_name", e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dlg_email">Email</Label>
            <Input
              id="dlg_email"
              type="email"
              value={data.email_address}
              onChange={(e) => setData("email_address", e.target.value)}
              required
            />
          </div>

          {!isEdit && (
            <p className="text-sm text-muted-foreground">
              Un email sera envoyé à l'utilisateur pour créer son mot de passe.
            </p>
          )}

          {current_user?.is_super_admin && (
            <div className="space-y-2">
              <Label htmlFor="dlg_organisation">Organisation</Label>
              <Select value={data.organisation_id} onValueChange={(v) => setData("organisation_id", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une organisation" />
                </SelectTrigger>
                <SelectContent>
                  {organisations.map((org) => (
                    <SelectItem key={org.id} value={String(org.id)}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="dlg_role">Rôle</Label>
            <Select value={data.role} onValueChange={(v) => setData("role", v as UserRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Utilisateur</SelectItem>
                <SelectItem value="admin">Administrateur</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="dlg_phone">Téléphone</Label>
              <Input
                id="dlg_phone"
                value={data.phone_number}
                onChange={(e) => setData("phone_number", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dlg_job_title">Poste</Label>
              <Input id="dlg_job_title" value={data.job_title} onChange={(e) => setData("job_title", e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch id="dlg_is_active" checked={data.is_active} onCheckedChange={(v) => setData("is_active", v)} />
            <Label htmlFor="dlg_is_active">Actif</Label>
          </div>

          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing}>
              {isEdit ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
